export interface IconRequestQueueOptions {
  /** Maximum number of loaders running at the same time. */
  concurrency?: number;
  /** How long a failed key is not requested again, in milliseconds. */
  retryDelayMs?: number;
  /** Clock, injectable for tests. */
  now?: () => number;
}

export interface IconRequestQueue {
  /**
   * Run `loader` for `key`, at most once at a time.
   * Returns the pending promise when `key` is already pending, and an already resolved promise
   * (without loading) when `key` failed less than `retryDelayMs` ago.
   * The returned promise never rejects, failures are remembered instead.
   */
  request(key: string, loader: () => Promise<void>): Promise<void>;
  /**
   * Mark `keys` as pending until `promise` settles, for requests that are not run by the queue
   * itself (e.g. a batch request). A failure is remembered like in `request`.
   */
  register(keys: string[], promise: Promise<unknown>): Promise<void>;
  /** Whether `key` is queued, running or registered. */
  isPending(key: string): boolean;
  /** Whether `key` failed less than `retryDelayMs` ago, so `request` would not load it. */
  isRecentlyFailed(key: string): boolean;
}

export function createIconRequestQueue(
  options: IconRequestQueueOptions = {},
): IconRequestQueue {
  const concurrency = options.concurrency ?? 6;
  const retryDelayMs = options.retryDelayMs ?? 30_000;
  const now = options.now ?? Date.now;

  const pending = new Map<string, Promise<void>>();
  const failedAt = new Map<string, number>();
  const waiting: Array<() => void> = [];
  let running = 0;

  function recentlyFailed(key: string): boolean {
    const at = failedAt.get(key);
    if (at === undefined) {
      return false;
    }
    if (now() - at >= retryDelayMs) {
      failedAt.delete(key);
      return false;
    }
    return true;
  }

  function settle(key: string, promise: Promise<unknown>): Promise<void> {
    const tracked = promise.then(
      () => {
        failedAt.delete(key);
      },
      () => {
        failedAt.set(key, now());
      },
    ).finally(() => {
      pending.delete(key);
    });
    pending.set(key, tracked);
    return tracked;
  }

  function next(): void {
    while (running < concurrency && waiting.length > 0) {
      running += 1;
      waiting.shift()!();
    }
  }

  function request(key: string, loader: () => Promise<void>): Promise<void> {
    const existing = pending.get(key);
    if (existing) {
      return existing;
    }
    if (recentlyFailed(key)) {
      return Promise.resolve();
    }
    const slot = new Promise<void>((start) => {
      waiting.push(start);
    }).then(() => loader()).finally(() => {
      running -= 1;
      next();
    });
    const tracked = settle(key, slot);
    next();
    return tracked;
  }

  function register(keys: string[], promise: Promise<unknown>): Promise<void> {
    return Promise.all(keys.map((key) => settle(key, promise))).then(() => undefined);
  }

  return {
    request,
    register,
    isPending: (key) => pending.has(key),
    isRecentlyFailed: recentlyFailed,
  };
}
